import React, { useEffect, useState } from "react";
import { fetchAllUsers,deleteUser } from "../api"; // Assuming fetchAllUsers is defined here
import AddUserModal from "./AddUserModal";
import PeoplePersonalPage from "./PeoplePersonalPage"; // Import the PeoplePersonalPage component
import { useAuth } from '../context/AuthContext';
import 'jspdf-autotable';
import { 
    FiUsers, FiSearch, FiPlus, FiDownload, FiFilter, FiX, 
    FiUser, FiMail, FiCheckCircle, FiTrash2 
} from 'react-icons/fi';

export default function PeoplePage() {
    const { user, isLoading: isAuthLoading } = useAuth();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false); // Renamed for clarity
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedUserId, setSelectedUserId] = useState(null); // Changed to store only the ID
    const [isPersonalPageModalOpen, setIsPersonalPageModalOpen] = useState(false); // New state for the personal page modal visibility
    const [successMessage, setSuccessMessage] = useState("");
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
const [visibleColumns, setVisibleColumns] = useState({
  name: true,
  accessLevel: true,
  email: true,
  training: true,
  visaStatus: true,
  employmentType: true,
});

const [showFilters, setShowFilters] = useState(false);
const [statusFilters, setStatusFilters] = useState({
  active: true,
  inactive: true,
  invited: true,
});

    // Dummy data for Training and Leave Balance as they are not in the current user object
    const getTrainingStatus = (email) => {
        if (email.includes("example.com")) return "Completed";
        if (email.includes("test.com")) return "In Progress";
        return "N/A";
    };

    // const getLeaveBalance = (email) => {
    //     const hash = email.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    //     return (hash % 10) + 5; // Between 5 and 14 days
    // };

    // Function to fetch users from the backend
    const fetchUsers = async () => {
        try {
            setLoading(true);
            const res = await fetchAllUsers();
            // console.log(res);

            // Only map the fields directly returned by fetchAllUsers
            const formattedUsers = res.data.data.map(user => ({
                _id: user._id, // Ensure _id is included for selection
                name: user.name,
                email: user.email,
                accessLevel: user.access,
                status: user.status, // Assuming status is returned by fetchAllUsers
                visaStatus: user.visaStatus,
                employmentType: user.employmentType,
                training: user.training,
                // leaveBalance: getLeaveBalance(user.email),
            }));

            setUsers(formattedUsers);
            setError("");
        } catch (err) {
            console.error("Error fetching users:", err);
            setError("Failed to load users. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    const handleUserAdded = () => {
        fetchUsers();
        setIsAddUserModalOpen(false);
    };

    const handleUserClick = (user) => {
        setSelectedUserId(user._id); // Store only the ID of the clicked user
        setIsPersonalPageModalOpen(true);
    };
    const handleDeleteUser = () => {
    if (!selectedUserId) return;
    setIsDeleteModalOpen(true);
};

const confirmDeleteUser = async () => {
    setLoading(true);
    setError(null);

    try {
        await deleteUser(selectedUserId);
        setUsers((prev) => prev.filter((user) => user._id !== selectedUserId));
        setSuccessMessage("User deleted successfully!");
        setIsDeleteModalOpen(false);
        setIsPersonalPageModalOpen(false);
        setSelectedUserId(null);
    } catch (err) {
        console.error("Failed to delete user:", {
            message: err.message,
            status: err.response?.status,
            data: err.response?.data,
        });
        setError(err.response?.data?.message || "Failed to delete user. Please try again.");
    } finally {
        setLoading(false);
        setTimeout(() => {
            setError(null);
            setSuccessMessage("");
        }, 5000);
    }
};
const handleClearSearch = () => {
    setSearchQuery('');
};

const getDotColor = (status) => {
  switch (status) {
    case "active":
      return "bg-green-500";
    case "inactive":
      return "bg-gray-400";
    case "invited":
      return "bg-yellow-400";
    default:
      return "bg-gray-300";
  }
};

    const isAdmin = user && user.role === 'admin';

    // Filtered users based on search query and status
    const filteredUsers = users.filter(user => {
        // Exclude admins
        if (user.accessLevel === 'admin') return false;
        
        // Status filter
        if (user.status && !statusFilters[user.status]) return false;
        
        // Search filter
        const searchLower = searchQuery.toLowerCase();
        const matchesSearch = !searchQuery ||
            (user.name && user.name.toLowerCase().includes(searchLower)) ||
            (user.email && user.email.toLowerCase().includes(searchLower)) ||
            (user.accessLevel && user.accessLevel.toLowerCase().includes(searchLower));
        
        return matchesSearch;
    });

    // Calculate stats for each status
    const statusCounts = users.reduce((acc, user) => {
        if (user.accessLevel !== 'admin' && user.status) {
            acc[user.status] = (acc[user.status] || 0) + 1;
        }
        return acc;
    }, { active: 0, inactive: 0, invited: 0 });

    const activeFilterCount = Object.values(statusFilters).filter(v => !v).length;
    const hasActiveFilters = activeFilterCount > 0 || searchQuery;

    const clearAllFilters = () => {
        setSearchQuery('');
        setStatusFilters({ active: true, inactive: true, invited: true });
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-gray-50">
            {/* Header */}
            <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-gray-200/50">
                <div className="px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/20">
                                <FiUsers className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <h1 className="text-xl font-bold text-gray-900">People</h1>
                                <p className="text-xs text-gray-500">{filteredUsers.length} of {users.length} members</p>
                            </div>
                        </div>
                        {isAdmin && (
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => {
                                        Promise.all([
                                            import("jspdf"),
                                            import("jspdf-autotable"),
                                        ]).then(([jsPDFModule, autoTableModule]) => {
                                            const jsPDF = jsPDFModule.default;
                                            const doc = new jsPDF();

                                            const tableColumn = Object.keys(visibleColumns)
                                                .filter((col) => visibleColumns[col])
                                                .map((col) => col.charAt(0).toUpperCase() + col.slice(1));

                                            const tableRows = filteredUsers.map((user) =>
                                                Object.keys(visibleColumns)
                                                    .filter((col) => visibleColumns[col])
                                                    .map((col) => user[col] || "N/A")
                                            );

                                            autoTableModule.default(doc, {
                                                head: [tableColumn],
                                                body: tableRows,
                                                startY: 20,
                                                styles: { fontSize: 9 },
                                                theme: "grid",
                                            });

                                            doc.setFontSize(14);
                                            doc.text("People Export", 14, 15);
                                            doc.save("people.pdf");
                                        });
                                    }}
                                    className="p-2.5 hover:bg-gray-100 rounded-xl transition-colors"
                                    title="Export to PDF"
                                >
                                    <FiDownload className="w-5 h-5 text-gray-600" />
                                </button>
                                <button
                                    onClick={() => setIsAddUserModalOpen(true)}
                                    className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-xl font-medium shadow-lg shadow-blue-500/25 transition-all hover:shadow-xl hover:shadow-blue-500/30 hover:scale-105"
                                >
                                    <FiPlus className="w-5 h-5" />
                                    <span className="hidden sm:inline">Add People</span>
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <div className="px-4 sm:px-6 lg:px-8 py-6">
                {/* Quick Stats Cards - Advanced UI/UX Feature */}
                {isAdmin && (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                        <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Active</p>
                                    <p className="text-2xl font-bold text-green-600 mt-1">{statusCounts.active || 0}</p>
                                </div>
                                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                                    <FiCheckCircle className="w-6 h-6 text-green-600" />
                                </div>
                            </div>
                        </div>
                        <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Invited</p>
                                    <p className="text-2xl font-bold text-yellow-600 mt-1">{statusCounts.invited || 0}</p>
                                </div>
                                <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
                                    <FiMail className="w-6 h-6 text-yellow-600" />
                                </div>
                            </div>
                        </div>
                        <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Inactive</p>
                                    <p className="text-2xl font-bold text-gray-600 mt-1">{statusCounts.inactive || 0}</p>
                                </div>
                                <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center">
                                    <FiUser className="w-6 h-6 text-gray-600" />
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Search and Filters */}
                <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm mb-4">
                    <div className="flex flex-col sm:flex-row gap-3">
                        <div className="relative flex-1">
                            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search by name, email, or access level..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-10 pr-10 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all text-sm"
                            />
                            {searchQuery && (
                                <button
                                    onClick={handleClearSearch}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-200 rounded-full transition-colors"
                                >
                                    <FiX className="w-4 h-4 text-gray-500" />
                                </button>
                            )}
                        </div>
                        <div className="relative">
                            <button
                                onClick={() => setShowFilters((prev) => !prev)}
                                className="flex items-center gap-2 px-4 py-3 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-xl transition-colors text-sm font-medium relative"
                            >
                                <FiFilter className="w-4 h-4 text-gray-600" />
                                <span>Filters</span>
                                {activeFilterCount > 0 && (
                                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-blue-600 text-white text-xs font-bold rounded-full flex items-center justify-center">
                                        {activeFilterCount}
                                    </span>
                                )}
                            </button>

                            {showFilters && (
                                <>
                                    <div
                                        className="fixed inset-0 z-0"
                                        onClick={() => setShowFilters(false)}
                                    ></div>
                                    <div className="absolute right-0 mt-2 bg-white border border-gray-200 rounded-xl shadow-xl p-4 z-10 min-w-[240px]">
                                        {/* Status Filters Section */}
                                        <div className="mb-4 pb-4 border-b border-gray-200">
                                            <h3 className="text-sm font-bold text-gray-900 mb-3">Status</h3>
                                            <div className="space-y-2">
                                                <label className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition-colors">
                                                    <input
                                                        type="checkbox"
                                                        checked={statusFilters.active}
                                                        onChange={() =>
                                                            setStatusFilters((prev) => ({
                                                                ...prev,
                                                                active: !prev.active,
                                                            }))
                                                        }
                                                        className="w-4 h-4 text-green-600 rounded focus:ring-2 focus:ring-green-500"
                                                    />
                                                    <span className="flex items-center gap-2 flex-1">
                                                        <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                                                        <span className="text-gray-700">Active</span>
                                                        <span className="ml-auto text-xs font-medium text-gray-500">({statusCounts.active || 0})</span>
                                                    </span>
                                                </label>
                                                <label className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition-colors">
                                                    <input
                                                        type="checkbox"
                                                        checked={statusFilters.invited}
                                                        onChange={() =>
                                                            setStatusFilters((prev) => ({
                                                                ...prev,
                                                                invited: !prev.invited,
                                                            }))
                                                        }
                                                        className="w-4 h-4 text-yellow-600 rounded focus:ring-2 focus:ring-yellow-500"
                                                    />
                                                    <span className="flex items-center gap-2 flex-1">
                                                        <span className="w-2 h-2 bg-yellow-400 rounded-full"></span>
                                                        <span className="text-gray-700">Invited</span>
                                                        <span className="ml-auto text-xs font-medium text-gray-500">({statusCounts.invited || 0})</span>
                                                    </span>
                                                </label>
                                                <label className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition-colors">
                                                    <input
                                                        type="checkbox"
                                                        checked={statusFilters.inactive}
                                                        onChange={() =>
                                                            setStatusFilters((prev) => ({
                                                                ...prev,
                                                                inactive: !prev.inactive,
                                                            }))
                                                        }
                                                        className="w-4 h-4 text-gray-600 rounded focus:ring-2 focus:ring-gray-500"
                                                    />
                                                    <span className="flex items-center gap-2 flex-1">
                                                        <span className="w-2 h-2 bg-gray-400 rounded-full"></span>
                                                        <span className="text-gray-700">Inactive</span>
                                                        <span className="ml-auto text-xs font-medium text-gray-500">({statusCounts.inactive || 0})</span>
                                                    </span>
                                                </label>
                                            </div>
                                        </div>

                                        {/* Column Visibility Section */}
                                        <div>
                                            <h3 className="text-sm font-bold text-gray-900 mb-3">Show Columns</h3>
                                            <div className="space-y-2">
                                                {Object.keys(visibleColumns).map((col) => (
                                                    <label key={col} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition-colors">
                                                        <input
                                                            type="checkbox"
                                                            checked={visibleColumns[col]}
                                                            onChange={() =>
                                                                setVisibleColumns((prev) => ({
                                                                    ...prev,
                                                                    [col]: !prev[col],
                                                                }))
                                                            }
                                                            className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                                                        />
                                                        <span className="text-gray-700">{col.charAt(0).toUpperCase() + col.slice(1)}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {/* Active Filter Chips - Advanced UI/UX Feature */}
                {hasActiveFilters && (
                    <div className="flex flex-wrap items-center gap-2 mb-4">
                        <span className="text-sm text-gray-600 font-medium">Active filters:</span>
                        {searchQuery && (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg text-sm font-medium">
                                <FiSearch className="w-3.5 h-3.5" />
                                "{searchQuery}"
                                <button
                                    onClick={() => setSearchQuery('')}
                                    className="hover:bg-blue-200 rounded-full p-0.5 transition-colors"
                                >
                                    <FiX className="w-3.5 h-3.5" />
                                </button>
                            </span>
                        )}
                        {!statusFilters.active && (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium">
                                <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                                Active hidden
                                <button
                                    onClick={() => setStatusFilters(prev => ({ ...prev, active: true }))}
                                    className="hover:bg-gray-200 rounded-full p-0.5 transition-colors"
                                >
                                    <FiX className="w-3.5 h-3.5" />
                                </button>
                            </span>
                        )}
                        {!statusFilters.invited && (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium">
                                <span className="w-2 h-2 bg-yellow-400 rounded-full"></span>
                                Invited hidden
                                <button
                                    onClick={() => setStatusFilters(prev => ({ ...prev, invited: true }))}
                                    className="hover:bg-gray-200 rounded-full p-0.5 transition-colors"
                                >
                                    <FiX className="w-3.5 h-3.5" />
                                </button>
                            </span>
                        )}
                        {!statusFilters.inactive && (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium">
                                <span className="w-2 h-2 bg-gray-400 rounded-full"></span>
                                Inactive hidden
                                <button
                                    onClick={() => setStatusFilters(prev => ({ ...prev, inactive: true }))}
                                    className="hover:bg-gray-200 rounded-full p-0.5 transition-colors"
                                >
                                    <FiX className="w-3.5 h-3.5" />
                                </button>
                            </span>
                        )}
                        <button
                            onClick={clearAllFilters}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-100 text-red-700 rounded-lg text-sm font-medium hover:bg-red-200 transition-colors"
                        >
                            <FiX className="w-3.5 h-3.5" />
                            Clear all
                        </button>
                    </div>
                )}

                {/* Alerts */}
                {successMessage && (
                    <div className="bg-green-50 border border-green-200 rounded-2xl p-4 mb-4 flex items-center gap-3 animate-fadeIn">
                        <FiCheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                        <div className="flex-1">
                            <p className="text-green-800 font-medium">Success!</p>
                            <p className="text-green-700 text-sm">{successMessage}</p>
                        </div>
                    </div>
                )}

                {error && (
                    <div className="bg-red-50 border border-red-200 rounded-2xl p-4 mb-4 flex items-center gap-3 animate-fadeIn">
                        <FiX className="w-5 h-5 text-red-600 flex-shrink-0" />
                        <div className="flex-1">
                            <p className="text-red-800 font-medium">Error!</p>
                            <p className="text-red-700 text-sm">{error}</p>
                        </div>
                    </div>
                )}

                {/* Content */}
                {loading ? (
                    <div className="space-y-3">
                        {Array.from({ length: 8 }).map((_, idx) => (
                            <div key={idx} className="bg-white rounded-2xl p-6 border border-gray-100 animate-pulse">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
                                    <div className="flex-1">
                                        <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
                                        <div className="h-3 bg-gray-200 rounded w-1/3"></div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : filteredUsers.length === 0 ? (
                    <div className="bg-white rounded-2xl p-12 text-center border border-gray-100">
                        <div className="w-20 h-20 bg-gray-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                            <FiUsers className="w-10 h-10 text-gray-400" />
                        </div>
                        <p className="text-gray-500 text-lg font-medium">No team members found</p>
                        <p className="text-gray-400 text-sm mt-1">
                            {searchQuery ? "Try adjusting your search" : "Add your first team member to get started!"}
                        </p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {filteredUsers.sort((a, b) => (a.name || '').localeCompare(b.name)).map((user, index) => {
                            // Generate user color
                            const colors = [
                                { from: '#a855f7', to: '#9333ea' }, // purple
                                { from: '#ec4899', to: '#db2777' }, // pink
                                { from: '#f97316', to: '#ea580c' }, // orange
                                { from: '#10b981', to: '#059669' }, // emerald
                                { from: '#14b8a6', to: '#0d9488' }, // teal
                                { from: '#0ea5e9', to: '#0284c7' }, // sky
                                { from: '#6366f1', to: '#4f46e5' }, // indigo
                                { from: '#d946ef', to: '#c026d3' }, // fuchsia
                            ];
                            let hash = 0;
                            for (let i = 0; i < user.name.length; i++) {
                                hash = user.name.charCodeAt(i) + ((hash << 5) - hash);
                            }
                            const colorIndex = Math.abs(hash) % colors.length;
                            const userColor = colors[colorIndex];

                            return (
                                <div
                                    key={user._id || index}
                                    className="bg-white rounded-2xl border border-gray-100 hover:shadow-lg transition-all duration-200 cursor-pointer overflow-hidden"
                                    onClick={() => handleUserClick(user)}
                                >
                                    <div className="p-5">
                                        <div className="flex items-center gap-4">
                                            <div 
                                                className="w-12 h-12 rounded-full flex items-center justify-center text-white text-lg font-semibold flex-shrink-0"
                                                style={{
                                                    background: `linear-gradient(to bottom right, ${userColor.from}, ${userColor.to})`
                                                }}
                                            >
                                                {user.name.charAt(0).toUpperCase()}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <h3 className="font-semibold text-gray-900 capitalize truncate">{user.name}</h3>
                                                    {isAdmin && user.status && (
                                                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                                                            user.status === 'active' ? 'bg-green-100 text-green-700' :
                                                            user.status === 'inactive' ? 'bg-gray-100 text-gray-700' :
                                                            user.status === 'invited' ? 'bg-yellow-100 text-yellow-700' :
                                                            'bg-gray-100 text-gray-700'
                                                        }`}>
                                                            {user.status}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-1 text-sm">
                                                    {visibleColumns.email && (
                                                        <div className="flex items-center gap-1.5 text-gray-600">
                                                            <FiMail className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                                                            <span className="truncate">{user.email}</span>
                                                        </div>
                                                    )}
                                                    {visibleColumns.accessLevel && (
                                                        <div className="flex items-center gap-1.5">
                                                            <span className="text-gray-500">Access:</span>
                                                            <span className="text-gray-700 font-medium capitalize">{user.accessLevel}</span>
                                                        </div>
                                                    )}
                                                    {visibleColumns.training && user.training && (
                                                        <div className="flex items-center gap-1.5">
                                                            <span className="text-gray-500">Training:</span>
                                                            <span className="text-gray-700">{user.training}</span>
                                                        </div>
                                                    )}
                                                    {visibleColumns.visaStatus && user.visaStatus && (
                                                        <div className="flex items-center gap-1.5">
                                                            <span className="text-gray-500">Visa:</span>
                                                            <span className="text-gray-700">{user.visaStatus}</span>
                                                        </div>
                                                    )}
                                                    {visibleColumns.employmentType && user.employmentType && (
                                                        <div className="flex items-center gap-1.5">
                                                            <span className="text-gray-500">Type:</span>
                                                            <span className="text-gray-700">{user.employmentType}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* The AddUserModal component */}
            <AddUserModal
                isOpen={isAddUserModalOpen}
                onClose={() => setIsAddUserModalOpen(false)}
                onUserAdded={handleUserAdded}
                id="add-user-modal"
            />

            {/* Full-screen PeoplePersonalPage Modal */}
            {isPersonalPageModalOpen && selectedUserId && user && user.role === 'admin'&& (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-50 p-0 sm:p-4 animate-fadeIn" onClick={() => {
                        setIsPersonalPageModalOpen(false);
                        setSelectedUserId(null);
                    }}>
                    <div className="relative bg-white rounded-3xl shadow-2xl w-full h-full sm:max-w-5xl sm:max-h-[95vh] overflow-hidden flex flex-col animate-slideUp" onClick={(e) => e.stopPropagation()}>
                        {/* Modal Header with modern design */}
                        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 shrink-0">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                    <div 
                                        className="w-16 h-16 rounded-2xl flex items-center justify-center text-white text-2xl font-bold shadow-lg"
                                        style={{
                                            background: 'linear-gradient(to bottom right, rgba(255,255,255,0.2), rgba(255,255,255,0.1))'
                                        }}
                                    >
                                        <FiUser className="w-8 h-8" />
                                    </div>
                                    <div>
                                        <h2 className="text-2xl font-bold">User Profile</h2>
                                        <p className="text-blue-100 text-sm">View and manage user information</p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => {
                                        setIsPersonalPageModalOpen(false);
                                        setSelectedUserId(null);
                                    }}
                                    className="p-2 hover:bg-white/20 rounded-xl transition-colors"
                                    aria-label="Close"
                                >
                                    <FiX className="w-6 h-6" />
                                </button>
                            </div>
                        </div>
                        
                        {/* Modal Body */}
                        <div className="flex-1 overflow-y-auto bg-gray-50">
                            <PeoplePersonalPage targetUserId={selectedUserId} />
                        </div>
                        
                        {/* Modal Footer with actions */}
                        <div className="p-4 bg-white border-t border-gray-200 flex justify-end gap-3 shrink-0 shadow-lg">
                            {isAdmin && (
                                <button
                                    onClick={handleDeleteUser}
                                    className="px-6 py-3 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white rounded-xl font-semibold shadow-lg shadow-red-500/25 transition-all hover:shadow-xl hover:shadow-red-500/30 flex items-center gap-2"
                                    disabled={loading}
                                >
                                    <FiTrash2 className="w-4 h-4" />
                                    Delete User
                                </button>
                            )}
                            <button
                                onClick={() => {
                                    setIsPersonalPageModalOpen(false);
                                    setSelectedUserId(null);
                                }}
                                className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-semibold transition-all"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Delete Confirmation Modal */}
            {isDeleteModalOpen && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
                    <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8 animate-slideUp">
                        <div className="w-16 h-16 bg-red-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                            <FiTrash2 className="w-8 h-8 text-red-600" />
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900 text-center mb-2">Delete User?</h2>
                        <p className="text-gray-600 text-center mb-6">
                            Are you sure you want to delete "{users.find(u => u._id === selectedUserId)?.name}"? This action cannot be undone.
                        </p>
                        {error && (
                            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4 flex items-center gap-3">
                                <FiX className="w-5 h-5 text-red-600 flex-shrink-0" />
                                <p className="text-red-700 text-sm">{error}</p>
                            </div>
                        )}
                        <div className="flex gap-3">
                            <button
                                onClick={() => {
                                    setIsDeleteModalOpen(false);
                                }}
                                className="flex-1 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-semibold transition-all"
                                disabled={loading}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmDeleteUser}
                                className="flex-1 px-6 py-3 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white rounded-xl font-semibold shadow-lg shadow-red-500/25 transition-all hover:shadow-xl hover:shadow-red-500/30 flex items-center justify-center gap-2"
                                disabled={loading}
                            >
                                {loading ? (
                                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                ) : (
                                    'Delete'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add custom CSS for animations */}
            <style jsx>{`
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                @keyframes slideUp {
                    from { 
                        opacity: 0;
                        transform: translateY(20px);
                    }
                    to { 
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                .animate-fadeIn {
                    animation: fadeIn 0.2s ease-out;
                }
                .animate-slideUp {
                    animation: slideUp 0.3s ease-out;
                }
            `}</style>
        </div>
    );
}






// import React, { useEffect, useState } from "react";
// import { fetchAllUsers,deleteUser } from "../api"; // Assuming fetchAllUsers is defined here
// import AddUserModal from "./AddUserModal";
// import PeoplePersonalPage from "./PeoplePersonalPage"; // Import the PeoplePersonalPage component
// import { useAuth } from '../context/AuthContext';
// import 'jspdf-autotable';

// export default function PeoplePage() {
//     const { user, isLoading: isAuthLoading } = useAuth();
//     const [users, setUsers] = useState([]);
//     const [loading, setLoading] = useState(true);
//     const [error, setError] = useState("");
//     const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false); // Renamed for clarity
//     const [searchQuery, setSearchQuery] = useState("");
//     const [selectedUserId, setSelectedUserId] = useState(null); // Changed to store only the ID
//     const [isPersonalPageModalOpen, setIsPersonalPageModalOpen] = useState(false); // New state for the personal page modal visibility
//     const [successMessage, setSuccessMessage] = useState("");
//     const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

//     // Dummy data for Training and Leave Balance as they are not in the current user object
//     const getTrainingStatus = (email) => {
//         if (email.includes("example.com")) return "Completed";
//         if (email.includes("test.com")) return "In Progress";
//         return "N/A";
//     };

//     // const getLeaveBalance = (email) => {
//     //     const hash = email.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
//     //     return (hash % 10) + 5; // Between 5 and 14 days
//     // };

//     // Function to fetch users from the backend
//     const fetchUsers = async () => {
//         try {
//             setLoading(true);
//             const res = await fetchAllUsers();
//             console.log(res)

//             // Only map the fields directly returned by fetchAllUsers
//             const formattedUsers = res.data.data.map(user => ({
//                 _id: user._id, // Ensure _id is included for selection
//                 name: user.name,
//                 email: user.email,
//                 accessLevel: user.access,
//                 status: user.status, // Assuming status is returned by fetchAllUsers
//                 visaStatus: user.visaStatus,
//                 employmentType: user.employmentType,
//                 training: user.training,
//                 // leaveBalance: getLeaveBalance(user.email),
//             }));

//             setUsers(formattedUsers);
//             setError("");
//         } catch (err) {
//             console.error("Error fetching users:", err);
//             setError("Failed to load users. Please try again.");
//         } finally {
//             setLoading(false);
//         }
//     };

//     useEffect(() => {
//         fetchUsers();
//     }, []);

//     const handleUserAdded = () => {
//         fetchUsers();
//         setIsAddUserModalOpen(false);
//     };

//     const handleUserClick = (user) => {
//         setSelectedUserId(user._id); // Store only the ID of the clicked user
//         setIsPersonalPageModalOpen(true);
//     };
//     const handleDeleteUser = () => {
//     if (!selectedUserId) return;
//     setIsDeleteModalOpen(true);
// };

// const confirmDeleteUser = async () => {
//     setLoading(true);
//     setError(null);

//     try {
//         await deleteUser(selectedUserId);
//         setUsers((prev) => prev.filter((user) => user._id !== selectedUserId));
//         setSuccessMessage("User deleted successfully!");
//         setIsDeleteModalOpen(false);
//         setIsPersonalPageModalOpen(false);
//         setSelectedUserId(null);
//     } catch (err) {
//         console.error("Failed to delete user:", {
//             message: err.message,
//             status: err.response?.status,
//             data: err.response?.data,
//         });
//         setError(err.response?.data?.message || "Failed to delete user. Please try again.");
//     } finally {
//         setLoading(false);
//         setTimeout(() => {
//             setError(null);
//             setSuccessMessage("");
//         }, 5000);
//     }
// };

//     const isAdmin = user && user.role === 'admin';

//     // Filtered users based on search query
//    const filteredUsers = users.filter(user =>
//         user.accessLevel !== 'admin' &&(
//         (user.name && user.name.toLowerCase().includes(searchQuery.toLowerCase())) ||
//         (user.email && user.email.toLowerCase().includes(searchQuery.toLowerCase())) ||
//         (user.accessLevel && user.accessLevel.toLowerCase().includes(searchQuery.toLowerCase()))
//         )
//     );

//     return (
//         <div className="flex flex-col md:flex-row min-h-screen bg-gray-100">
//             {/* Left Sidebar */}
//             <aside className="w-full md:w-64 bg-white p-4 md:p-6 shadow-md border-b md:border-r border-gray-200">
//                 <div className="mb-4 md:mb-8">
//                     <h2 className="text-xl font-bold text-gray-800 mb-2 md:mb-4 flex items-center">
//                         <svg className="w-6 h-6 mr-2 text-gray-600" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
//                             <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd"></path>
//                         </svg>
//                         Team Members
//                     </h2>
//                     {isAdmin && (
//                         <button
//                             onClick={() => setIsAddUserModalOpen(true)}
//                             className="w-full bg-blue-600 text-white px-3 py-2 rounded hover:bg-blue-700 transition duration-200 text-sm font-medium"
//                             aria-haspopup="dialog"
//                             aria-expanded={isAddUserModalOpen}
//                             aria-controls="add-user-modal"
//                         >
//                             Add People
//                         </button>
//                     )}
//                 </div>
//                 <nav className="hidden md:block space-y-6">
//                     {/* ... (your commented out sidebar nav items) ... */}
//                 </nav>
//             </aside>

//             {/* Main Content Area */}
//             <main className="flex-1 p-4 md:p-8">
//                 <div className="mb-4 md:mb-6">
//                     <h1 className="text-2xl font-semibold text-gray-800">People</h1>
//                     <p className="text-sm text-gray-600">Showing {filteredUsers.length} Out of {users.length} Users</p>
//                 </div>

//                 <div className="flex flex-col sm:flex-row items-center space-y-4 sm:space-y-0 sm:space-x-4 mb-6">
//                     <div className="relative flex-grow w-full sm:w-auto">
//                         <input
//                             type="text"
//                             placeholder="Search..."
//                             className="pl-10 pr-4 py-2 border border-gray-300 rounded-md w-full focus:outline-none focus:ring-1 focus:ring-blue-500"
//                             value={searchQuery}
//                             onChange={(e) => setSearchQuery(e.target.value)}
//                         />
//                         <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
//                             <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd"></path>
//                         </svg>
//                     </div>
//                     {isAdmin && (
//                         <button
//                             className="flex items-center bg-blue-50 border border-blue-400 text-blue-600 px-4 py-2 rounded-md hover:bg-blue-100 transition duration-200 text-sm font-medium w-full sm:w-auto justify-center"
//                             onClick={() => {
//                                 Promise.all([
//                                     import('jspdf'),
//                                     import('jspdf-autotable')
//                                 ]).then(([jsPDFModule, autoTableModule]) => {
//                                     const jsPDF = jsPDFModule.default;
//                                     const doc = new jsPDF();
//                                     const tableColumn = [
//                                         "Name",
//                                         "Access",
//                                         "Email",
//                                         "Training",
//                                         "Visa Status",
//                                         "Employment Type"
//                                     ];
//                                     const tableRows = filteredUsers.map(user => [
//                                         user.name,
//                                         user.accessLevel,
//                                         user.email,
//                                         user.training || 'N/A',
//                                         user.leaveBalance,
//                                         user.visaStatus || 'N/A',
//                                         user.employmentType || 'N/A'
//                                     ]);
//                                     autoTableModule.default(doc, {
//                                         head: [tableColumn],
//                                         body: tableRows,
//                                         startY: 20,
//                                         styles: { fontSize: 9 }
//                                     });
//                                     doc.text("People Export", 14, 15);
//                                     doc.save("people.pdf");
//                                 });
//                             }}
//                         >
//                             <span>
//                                 <img src="/outbox.svg" alt="Export icon" className='w-[20px] mr-1 text-blue-600'/>
//                             </span>
//                             Export
//                         </button>
//                     )}
//                 </div>

//                 {error && (
//                     <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
//                         <strong className="font-bold">Error!</strong>
//                         <span className="block sm:inline"> {error}</span>
//                     </div>
//                 )}

//                 {loading ? (
//                     <p className="text-gray-500">Loading users...</p>
//                 ) : filteredUsers.length === 0 ? (
//                     <p className="text-gray-500">No users found matching your search.</p>
//                 ) : (
//                     <>
//                         {/* Table for larger screens */}
//                         <div className="hidden md:block bg-white shadow rounded-md overflow-x-auto">
//                             <table className="min-w-full text-left table-auto">
//                                 <thead className="bg-gray-50 border-b border-gray-200">
//                                     <tr>
//                                         <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
//                                         <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Access</th>
//                                         <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
//                                         <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Training</th>
//                                         <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Visa Status</th>
//                                         <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Employment Type</th>
//                                     </tr>
//                                 </thead>
//                                 <tbody className="divide-y divide-gray-200">
//                                     {filteredUsers.map((user, index) => (
//                                         <tr
//                                             key={user._id || index} // Use _id for key
//                                             className="hover:bg-gray-50 transition-colors duration-150 cursor-pointer"
//                                             onClick={() => handleUserClick(user)}
//                                         >
//                                             <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600 underline">{user.name}</td>
//                                             <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 capitalize">{user.accessLevel}</td>
//                                             <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{user.email}</td>
//                                             <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{user.training || 'N/A'}</td>
//                                             {/* <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{user.leaveBalance}</td> */}
//                                             <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{user.visaStatus || 'N/A'}</td>
//                                             <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{user.employmentType || 'N/A'}</td>
//                                         </tr>
//                                     ))}
//                                 </tbody>
//                             </table>
//                         </div>

//                         {/* Card view for mobile screens */}
//                         <div className="md:hidden grid grid-cols-1 gap-4">
//                             {filteredUsers.map((user, index) => (
//                                 <div
//                                     key={user._id || index} // Use _id for key
//                                     className="bg-white shadow rounded-md p-4 border border-gray-200 cursor-pointer"
//                                     onClick={() => handleUserClick(user)}
//                                 >
//                                     <div className="flex justify-between items-center mb-2">
//                                         <h3 className="text-lg font-semibold text-blue-600 underline">{user.name}</h3>
//                                         <span className="text-sm font-medium text-gray-600 capitalize">{user.accessLevel}</span>
//                                     </div>
//                                     <div className="space-y-1 text-sm text-gray-700">
//                                         <p><span className="font-medium">Email:</span> {user.email}</p>
//                                         <p><span className="font-medium">Training:</span> {user.training || 'N/A'}</p>
//                                         <p><span className="font-medium">Leave Balance:</span> {user.leaveBalance}</p>
//                                         <p><span className="font-medium">Visa Status:</span> {user.visaStatus || 'N/A'}</p>
//                                         <p><span className="font-medium">Employment Type:</span> {user.employmentType || 'N/A'}</p>
//                                     </div>
//                                 </div>
//                             ))}
//                         </div>
//                     </>
//                 )}
//             </main>

//             {/* The AddUserModal component */}
//             <AddUserModal
//                 isOpen={isAddUserModalOpen}
//                 onClose={() => setIsAddUserModalOpen(false)}
//                 onUserAdded={handleUserAdded}
//                 id="add-user-modal"
//             />

//             {/* Full-screen PeoplePersonalPage Modal */}

//             {isPersonalPageModalOpen && selectedUserId && user && user.role === 'admin'&& (
//     <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex justify-center items-center z-50 p-0 sm:p-4">
//         <div className="relative bg-white rounded-lg shadow-xl w-full h-full sm:max-w-4xl sm:max-h-[90vh] overflow-hidden flex flex-col">
//             <div className="flex justify-between items-center bg-blue-600 text-white p-4 shrink-0">
//                 <h2 className="text-xl font-semibold">Personal Details</h2>
//                 <button
//                     onClick={() => {
//                         setIsPersonalPageModalOpen(false);
//                         setSelectedUserId(null);
//                     }}
//                     className="text-white hover:text-gray-200 focus:outline-none"
//                     aria-label="Close"
//                 >
//                     <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
//                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
//                     </svg>
//                 </button>
//             </div>
//             <div className="flex-1 overflow-y-auto">
//                 <PeoplePersonalPage targetUserId={selectedUserId} />
//             </div>
//             <div className="p-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-2 shrink-0">
//                 {isAdmin && (
//                     <button
//                         onClick={handleDeleteUser}
//                         className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors text-sm font-medium"
//                         disabled={loading}
//                     >
//                         Delete
//                     </button>
//                 )}
//                 <button
//                     onClick={() => {
//                         setIsPersonalPageModalOpen(false);
//                         setSelectedUserId(null);
//                     }}
//                     className="bg-gray-300 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-400 transition-colors text-sm font-medium"
//                 >
//                     Cancel
//                 </button>
//             </div>
//         </div>
//     </div>
// )}
// {isDeleteModalOpen && (
//     <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50 p-4">
//         <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm md:max-w-md p-6 md:p-8 transform transition-all duration-300 ease-out scale-95 opacity-0 animate-scale-in">
//             <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 md:mb-6 text-center">
//                 Confirm Deletion
//             </h2>
//             <p className="text-sm text-gray-700 mb-6 text-center">
//                 Are you sure you want to delete the user "{users.find(u => u._id === selectedUserId)?.name}"? This action cannot be undone.
//             </p>
//             {error && (
//                 <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4 text-sm" role="alert">
//                     <strong className="font-bold">Error: </strong>
//                     <span className="block sm:inline">{error}</span>
//                 </div>
//             )}
//             <div className="flex justify-end space-x-2">
//                 <button
//                     onClick={() => {
//                         setIsDeleteModalOpen(false);
//                     }}
//                     className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition duration-200 text-base font-medium"
//                     disabled={loading}
//                 >
//                     Cancel
//                 </button>
//                 <button
//                     onClick={confirmDeleteUser}
//                     className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition duration-200 text-base font-medium flex items-center justify-center"
//                     disabled={loading}
//                 >
//                     {loading ? (
//                         <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
//                             <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
//                             <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
//                         </svg>
//                     ) : (
//                         'Delete'
//                     )}
//                 </button>
//             </div>
//         </div>
//     </div>
// )}
//         </div>
//     );
// }
